import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  Image, Film, FileText, Loader2, Trash2, Upload, Camera, Video, Star
} from 'lucide-react';
import { getImageUrl } from '../../utils/api';
import './MediaVault.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  {
    key: 'editorial_portrait',
    label: 'Editorial Portraits',
    hint: 'Press-quality photos for platform profiles and features (3-5 photos, hi-res)',
    icon: Camera,
    accept: 'image/jpeg,image/png,image/webp',
    type: 'image',
    max: 5,
  },
  {
    key: 'lifestyle',
    label: 'Lifestyle / Candid',
    hint: 'Real moments — studio, performing, in your world (3-5 photos)',
    icon: Image,
    accept: 'image/jpeg,image/png,image/webp',
    type: 'image',
    max: 5,
  },
  {
    key: 'video_clip',
    label: 'Video Clips',
    hint: 'Fan-facing, DJ-facing, behind-the-song, studio clips (MP4, MOV)',
    icon: Video,
    accept: 'video/mp4,video/quicktime,video/x-msvideo,video/webm',
    type: 'video',
    max: 10,
  },
  {
    key: 'press_material',
    label: 'Press Material',
    hint: 'PDFs, press releases, EPK documents, one-sheets',
    icon: FileText,
    accept: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp',
    type: 'doc',
    max: 10,
  },
  {
    key: 'logo',
    label: 'Logo / Wordmark',
    hint: 'Vector or hi-res PNG of your logo',
    icon: Star,
    accept: 'image/png,image/svg+xml,image/webp,image/jpeg',
    type: 'image',
    max: 2,
  },
];

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getProxiedUrl = (url) => {
  if (!url) return '';
  return getImageUrl(url) || url;
};

const MediaVault = () => {
  const [media, setMedia] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  const getToken = () => localStorage.getItem('artistToken');
  const getHeaders = () => ({ 'Authorization': `Bearer ${getToken()}` });

  useEffect(() => { fetchMedia(); }, []);

  const fetchMedia = async () => {
    try {
      const res = await fetch(`${API_URL}/api/artist/media`, { headers: getHeaders() });
      if (res.ok) setMedia(await res.json());
    } catch {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (category, file) => {
    setUploading(category);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/artist/media/upload?category=${category}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData,
      });

      if (res.ok) {
        toast.success('File uploaded');
        fetchMedia();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      const res = await fetch(`${API_URL}/api/artist/media/${fileId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (res.ok) {
        toast.success('File deleted');
        fetchMedia();
      } else {
        toast.error('Delete failed');
      }
    } catch {
      toast.error('Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="media-vault">
        <div className="mv-loading"><Loader2 size={24} className="spinner" /> Loading media...</div>
      </div>
    );
  }

  return (
    <div className="media-vault" data-testid="media-vault">
      <div className="mv-header">
        <h1>Media Vault</h1>
        <p>Upload editorial portraits, lifestyle shots, video clips, press material, and your logo</p>
      </div>

      {CATEGORIES.map(cat => {
        const files = media[cat.key] || [];
        const atLimit = files.length >= cat.max;
        const isUploading = uploading === cat.key;
        const Icon = cat.icon;

        return (
          <div key={cat.key} className="mv-category" data-testid={`mv-cat-${cat.key}`}>
            <div className="mv-cat-header">
              <div className="mv-cat-title">
                <Icon size={18} />
                <h3>{cat.label}</h3>
              </div>
              <span className="mv-cat-count">{files.length} / {cat.max}</span>
            </div>
            <p className="mv-cat-hint">{cat.hint}</p>

            {/* Upload zone */}
            <div className={`mv-upload-zone${atLimit ? ' disabled' : ''}`}>
              <input
                type="file"
                accept={cat.accept}
                disabled={atLimit || isUploading}
                data-testid={`mv-upload-${cat.key}`}
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(cat.key, file);
                  e.target.value = '';
                }}
              />
              <div className="mv-upload-icon"><Upload size={22} /></div>
              <p className="mv-upload-text">
                {atLimit
                  ? `Maximum ${cat.max} files reached`
                  : <>Drop file here or <strong>click to upload</strong></>}
              </p>
            </div>

            {isUploading && (
              <div className="mv-uploading">
                <Loader2 size={16} className="spinner" /> Uploading...
              </div>
            )}

            {/* Files */}
            {files.length > 0 && (
              cat.type === 'doc' ? (
                <div className="mv-files doc-list">
                  {files.map(f => (
                    <div key={f.id} className="mv-doc-row" data-testid={`mv-file-${f.id}`}>
                      <FileText size={18} />
                      <span className="mv-file-name">{f.original_name}</span>
                      <span className="mv-file-size">{formatFileSize(f.file_size)}</span>
                      <a href={getProxiedUrl(f.file_url)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--neon-cyan)', fontSize: '0.72rem', textDecoration: 'none', fontWeight: 600 }}>
                        View
                      </a>
                      <button className="mv-file-delete" onClick={() => handleDelete(f.id)} data-testid={`mv-delete-${f.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`mv-files${cat.type === 'video' ? ' video-grid' : ''}`}>
                  {files.map(f => (
                    <div key={f.id} className="mv-file-card" data-testid={`mv-file-${f.id}`}>
                      {cat.type === 'image' ? (
                        <img
                          src={getProxiedUrl(f.file_url)}
                          alt={f.original_name}
                          className="mv-file-thumb"
                          onError={e => { e.target.style.background = 'rgba(255,255,255,0.05)'; }}
                        />
                      ) : cat.type === 'video' ? (
                        <div className="mv-file-video-thumb">
                          <Film size={32} />
                        </div>
                      ) : (
                        <div className="mv-file-doc-thumb">
                          <FileText size={32} />
                        </div>
                      )}
                      <div className="mv-file-info">
                        <div className="mv-file-name" title={f.original_name}>{f.original_name}</div>
                        <div className="mv-file-size">{formatFileSize(f.file_size)}</div>
                      </div>
                      <button className="mv-file-delete" onClick={() => handleDelete(f.id)} data-testid={`mv-delete-${f.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MediaVault;
